import TysiaText from 'DesignSystem/TysiaText/index'
import DayPicker, { DateUtils } from 'react-day-picker'
import 'react-day-picker/lib/style.css'
import './day-picker.css'
import React, { useEffect, useState } from 'react'
import {
  differenceInCalendarMonths,
  parse,
  parseISO,
  isValid,
  isBefore,
  isAfter,
  isSameDay,
  isSameMonth,
  addMonths,
} from 'date-fns'
import TysiaInputContent from 'DesignSystem/TysiaInput/TysiaInputContent/index'
import TysiaInputFieldMasked from 'DesignSystem/TysiaInput/TysiaInputFieldMasked/index'
import TysiaInput from 'DesignSystem/TysiaInput/index'
import { dateISOIn } from 'Data/format.js'

function YearMonthForm({ date, localeUtils, onChange, yearPastFrom, yearTo }) {
  let months = localeUtils.getMonths()
  let fromMonth = new Date(date.getFullYear() - yearPastFrom, 0)
  let toMonth = new Date(date.getFullYear() + yearTo, 11)

  let years = []
  for (let i = fromMonth.getFullYear(); i <= toMonth.getFullYear(); i += 1) {
    years.push(i)
  }

  function handleChange(e) {
    let { year, month } = e.target.form
    let date = new Date(year.value, month.value)

    onChange(date)
  }

  return (
    <>
      <form className="DayPicker-Caption">
        <div className="select-wrapper">
          <select name="month" onChange={handleChange} value={date.getMonth()}>
            {months.map((month, i) => (
              <option key={month} value={i}>
                {month}
              </option>
            ))}
          </select>
        </div>
        <div className="select-wrapper">
          <select
            name="year"
            onChange={handleChange}
            value={date.getFullYear()}
          >
            {years.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </form>
    </>
  )
}

function toDate(date) {
  if (date === undefined || date === null) {
    return date
  }

  let d

  if (date instanceof Date) {
    d = date
  } else if (typeof date === 'number') {
    d = new Date(date)
  } else if (typeof date === 'string' && date.length) {
    if (date.includes('_')) {
      return date
    }
    d = date.includes('/')
      ? parse(date, 'MM/dd/yyyy', new Date())
      : parseISO(date)
  } else {
    return null
  }

  if (!isValid(d)) {
    return null
  }

  return d
}

export default function DateRangePicker({
  viewPath,
  value,
  onChange,
  disableFuture = false,
  disablePast = false,
  datePast = new Date(),
  dateFuture = new Date(),
  yearPicker = true,
  yearPastFrom = 100,
  yearTo = 10,
  numberOfMonths = 2,
}) {
  let from = value?.from
  let to = value?.to

  let [fromMonth, setFromMonth] = useState(from || new Date())
  let [toMonth, setToMonth] = useState(
    to && !isSameMonth(to, fromMonth) ? to : addMonths(fromMonth, 1)
  )

  let modifiers = { start: from, end: to }

  let [dateInput, setDateInput] = useState({
    from: formatDate(from),
    to: formatDate(to),
  })

  function handleDayClick(day) {
    let range
    if (from && to) {
      // reset the selection from scratch if they select a day after selecting both from and to days
      range = DateUtils.addDayToRange(day, { from: undefined, to: undefined })
    } else {
      range = DateUtils.addDayToRange(day, { from, to })
    }

    onChange && onChange(range)

    setDateInput({
      from: formatDate(range.from),
      to: formatDate(range.to),
    })
  }

  useEffect(() => {
    if (
      !isSameDay(from, parseISO(dateInput.from)) ||
      !isSameDay(to, parseISO(dateInput.to))
    )
      setDateInput({
        from: formatDate(from),
        to: formatDate(to),
      })
  }, [from, to])

  function formatDate(date) {
    return date && isValid(date) ? dateISOIn(date) : date
  }

  function onChangeInput(date, isFromCalendar) {
    let day = toDate(date)

    // If the date is invalid, update the input fields and stop execution.
    if (!isValid(day)) {
      setDateInput(state => ({
        from: isFromCalendar ? formatDate(date) : state.from,
        to: !isFromCalendar ? formatDate(date) : state.to,
      }))
      return
    }

    let range = calculateDateRange(day, isFromCalendar, from, to)

    // Update the date range in your application state.
    onChange(range)
    setFromMonth(range.from || fromMonth)
    setToMonth(
      range.from && range.to && isSameMonth(range.from, range.to)
        ? addMonths(range.from, 1)
        : range.to
        ? range.to
        : toMonth
    )

    // Update the displayed date input fields.
    setDateInput({
      from: formatDate(range.from),
      to: formatDate(range.to),
    })
  }

  /**
   * Calculates the appropriate date range based on the selected day and input source.
   * @param {Date} day - The date selected by the user.
   * @param {boolean} isFromCalendar - True if the date came from 'Start Date' calendar, false otherwise (e.g., direct input).
   * @param {Date} currentFrom - The current 'from' date in the range.
   * @param {Date} currentTo - The current 'to' date in the range.
   * @returns {{from: Date, to: Date}} The calculated date range.
   */
  function calculateDateRange(day, isFromCalendar, currentFrom, currentTo) {
    // Scenario 1: 'day' is within the existing range
    if (isAfter(day, currentFrom) && isBefore(day, currentTo)) {
      // If from calendar, new range starts/ends at 'day'. Otherwise, maintains original start/end.
      return {
        from: isFromCalendar ? day : currentFrom,
        to: !isFromCalendar ? day : currentTo,
      }
    }

    // Scenario 2: 'day' is before the current 'from' date
    if (isBefore(day, currentFrom)) {
      // If from calendar, new range starts at 'day' and ends at current 'to'.
      // Otherwise, new range starts at 'day' and ends at current 'from'.
      return {
        from: day,
        to: isFromCalendar ? currentTo : currentFrom,
      }
    }

    // Scenario 3: 'day' is after the current 'to' date
    if (isAfter(day, currentTo)) {
      // If from calendar, new range starts at current 'to' and ends at 'day'.
      // Otherwise, new range starts at current 'from' and ends at 'day'.
      return {
        from: isFromCalendar ? currentTo : currentFrom,
        to: day,
      }
    }

    // Scenario 4: 'day' is exactly 'from' or 'to' (or no range exists yet)
    // This covers cases where 'day' is equal to 'from' or 'to', or if 'from'/'to' are undefined initially.
    return DateUtils.addDayToRange(day, {
      from: currentFrom,
      to: currentTo,
    })
  }

  return (
    <div className="range-picker">
      {[...Array(numberOfMonths)].map((_, i) => {
        let isFromCalendar = i === 0
        let displayMonth = new Date(isFromCalendar ? fromMonth : toMonth)

        function onYearMonthChange(date) {
          if (isFromCalendar) {
            setFromMonth(date)
            setToMonth(DateUtils.addMonths(date, 1))
          } else {
            let difference = differenceInCalendarMonths(date, fromMonth)

            if (difference > 0) {
              setToMonth(date)
            } else if (difference < 0) {
              setToMonth(fromMonth)
              setFromMonth(date)
            }
          }
        }

        return (
          <div key={i} className="calendar-with-title">
            <TysiaText
              viewPath={`${viewPath}/CalendarTitle${i}`}
              text={isFromCalendar ? 'Start Date' : 'End Date'}
              weight="medium"
              paddingBottom={4}
            />
            <TysiaInput
              viewPath={`${viewPath}/TysiaInput${i}`}
              marginTop={0}
              marginBottom={8}
              width="auto"
              flexGrow={0}
              autoFocus={isFromCalendar}
              invalid={
                isFromCalendar ? !toDate(dateInput.from) : !toDate(dateInput.to)
              }
              type="text"
              size="small"
              paddingLeft={0}
              paddingTop={8}
              backgroundColor="transparent"
              onChange={date => {
                if (
                  (isFromCalendar && date !== dateInput.from) ||
                  (!isFromCalendar && date !== dateInput.to)
                )
                  onChangeInput(date, isFromCalendar)
              }}
              value={isFromCalendar ? dateInput.from : dateInput.to}
            >
              {childProps => (
                <TysiaInputContent
                  {...childProps}
                  viewPath={`${viewPath}/TysiaInputContent${i}`}
                  paddingLeft={0}
                >
                  {childProps => (
                    <TysiaInputFieldMasked
                      {...childProps}
                      viewPath={`${viewPath}/TysiaInputFieldMasked${i}`}
                      mask="date"
                      placeholder="mm/dd/yyyy"
                    />
                  )}
                </TysiaInputContent>
              )}
            </TysiaInput>
            <DayPicker
              key={i}
              month={displayMonth}
              selectedDays={[from, { from, to }]}
              onDayClick={handleDayClick}
              modifiers={modifiers}
              disabledDays={{
                after: (disableFuture && dateFuture) || (isFromCalendar && to),
                before: (disablePast && datePast) || (!isFromCalendar && from),
              }}
              toMonth={isFromCalendar ? to : undefined}
              fromMonth={!isFromCalendar ? from : undefined}
              captionElement={
                yearPicker
                  ? ({ date, localeUtils }) => (
                      <YearMonthForm
                        date={date}
                        yearPastFrom={yearPastFrom}
                        yearTo={yearTo}
                        localeUtils={localeUtils}
                        onChange={onYearMonthChange}
                        fromMonthNumber={
                          !isFromCalendar
                            ? new Date(from).getMonth()
                            : undefined
                        }
                        isFromCalendar={isFromCalendar}
                      />
                    )
                  : undefined
              }
            />
          </div>
        )
      })}
    </div>
  )
}
